import React, { useState, useEffect, Fragment } from 'react';
// ⚠ MODIFIED FROM PORT — Phase 4 admin actions
import { api } from '../api.js';
import { useT } from '../i18n.js';
import { fmtTHB, netPayableOf, parseMoney } from './data.jsx';
import { MoneyInput } from './money-input.jsx';
import { IconArrow, IconBank, IconBell, IconCal, IconCheck, IconChevron, IconReceipt, IconSearch, IconUsers, IconX } from './icons.jsx';
import { Avatar, StatusPill } from './screens-internal.jsx';

import { useState as useStateMod, useEffect as useEffectMod } from 'react';

/* ============================================================
   Modal — generic backdrop + centered card with Esc close
   ============================================================ */
export function Modal({ open, onClose, width = 560, children }) {
  useEffectMod(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "var(--bg-overlay)",
        backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
        zIndex: 1200,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
        animation: "modal-back-in 200ms var(--easing-emphasized)",
      }}>
      <style>{`
        @keyframes modal-back-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes modal-card-in {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
      `}</style>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: width,
          maxHeight: "calc(100vh - 48px)",
          background: "var(--bg-secondary)",
          border: "1px solid var(--border-default)",
          borderRadius: "var(--radius-xl)",
          boxShadow: "var(--elevation-3)",
          overflow: "hidden",
          display: "flex", flexDirection: "column",
          animation: "modal-card-in 220ms var(--easing-emphasized)",
        }}>
        {children}
      </div>
    </div>
  );
}

export function ModalHeader({ title, subtitle, onClose }) {
  return (
    <div style={{
      padding: "18px 22px",
      borderBottom: "1px solid var(--border-subtle)",
      display: "flex", alignItems: "flex-start", gap: 12,
    }}>
      <div style={{flex: 1, minWidth: 0}}>
        <div style={{fontSize: 16, fontWeight: 500, letterSpacing: "-0.005em"}}>{title}</div>
        {subtitle && <div className="muted" style={{fontSize: 12, marginTop: 4}}>{subtitle}</div>}
      </div>
      <button className="icon-btn" onClick={onClose} style={{flexShrink: 0}}><IconX size={16}/></button>
    </div>
  );
}

export function ModalFooter({ children }) {
  return (
    <div style={{
      padding: "12px 22px",
      borderTop: "1px solid var(--border-subtle)",
      background: "var(--bg-primary)",
      display: "flex", justifyContent: "flex-end", gap: 8, flexShrink: 0,
    }}>{children}</div>
  );
}

/* ============================================================
   CONFIRM — generic confirmation dialog (reusable)
   ============================================================ */
export function ConfirmModal({ open, title, subtitle, body, confirmLabel, danger = false, busy = false, onConfirm, onClose }) {
  const t = useT();
  if (!open) return null;
  return (
    <Modal open={open} onClose={busy ? () => {} : onClose} width={460}>
      <ModalHeader title={title} subtitle={subtitle} onClose={busy ? () => {} : onClose}/>
      {body && (
        <div style={{padding: "18px 22px", fontSize: 13, color: "var(--text-secondary)", lineHeight: 1.6}}>{body}</div>
      )}
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>{t('Cancel')}</button>
        <button className="btn btn-primary btn-sm" onClick={onConfirm} disabled={busy}
          style={danger ? { background: 'var(--danger)', borderColor: 'var(--danger)', color: '#fff' } : undefined}>
          {busy ? t('Working…') : (confirmLabel || t('Confirm'))}
        </button>
      </ModalFooter>
    </Modal>
  );
}

/* ============================================================
   INVITE TEAMMATE
   ============================================================ */
// ⚠ MODIFIED FROM PORT — wires to api.addUser (admin adds a staff teammate
// to the Users sheet). The prototype's "supplier teammate" framing is dropped
// because that flow doesn't exist yet — for now this is staff-side invitation.
export function InviteModal({ open, onClose, onAdded }) {
  const t = useT();
  const [role, setRole] = useStateMod("pm");
  const [email, setEmail] = useStateMod("");
  const [name, setName] = useStateMod("");
  const [busy, setBusy] = useStateMod(false);
  useEffectMod(() => { if (open) { setRole("pm"); setEmail(""); setName(""); setBusy(false); } }, [open]);
  const ROLES = [
    { id: "admin",   label: "Admin",   desc: "Full access — vendors, invoices, billing, team",       perms: ["All actions"] },
    { id: "pm",      label: "Project Manager", desc: "Approve invoices, register vendors, send portal links", perms: ["Approve invoices", "Vendor registration", "Send portal links"] },
    { id: "finance", label: "Finance", desc: "Verify invoices, schedule payments, manage compliance", perms: ["Finance verify", "Pay runs", "Banking"] },
    { id: "viewer",  label: "Viewer",  desc: "Read-only access to vendors and invoices",             perms: ["Read vendors", "Read invoices"] },
  ];
  const submit = async () => {
    if (!email.trim() || !name.trim()) {
      window.__toast?.('Email and full name are required.', 'error');
      return;
    }
    setBusy(true);
    try {
      await api.addUser({ email: email.trim(), name: name.trim(), role });
      window.__toast?.(`${name.trim()} added as ${role}`, 'success');
      onAdded?.();
      onClose();
    } catch (e) {
      window.__toast?.(`Failed: ${e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={open} onClose={busy ? () => {} : onClose} width={520}>
      <ModalHeader title={t("Add team member")} subtitle={t("Adds a row to the Users tab so this Google account can sign in to the staff app.")} onClose={busy ? () => {} : onClose}/>
      <div style={{padding: 22, overflowY: "auto"}}>
        <div className="form-grid">
          <div className="field full">
            <label className="field-label">{t("Email address")} <span style={{color:"var(--danger)"}}>*</span></label>
            <input className="input" placeholder="colleague@motionreach.co.th" value={email} onChange={e => setEmail(e.target.value)}/>
            <span className="field-help">{t("Must match the Google account they'll sign in with.")}</span>
          </div>
          <div className="field full">
            <label className="field-label">{t("Full name")} <span style={{color:"var(--danger)"}}>*</span></label>
            <input className="input" placeholder={t("Their display name")} value={name} onChange={e => setName(e.target.value)}/>
          </div>
        </div>

        <div style={{height: 1, background: "var(--border-subtle)", margin: "18px 0"}}></div>

        <div className="field-label" style={{marginBottom: 10}}>{t("Permission role")} <span style={{color:"var(--danger)"}}>*</span></div>
        <div style={{display: "flex", flexDirection: "column", gap: 8}}>
          {ROLES.map(r => (
            <label key={r.id} style={{
              display: "grid",
              gridTemplateColumns: "24px 1fr auto",
              gap: 12,
              padding: 14,
              border: "1px solid " + (role === r.id ? "var(--accent)" : "var(--border-subtle)"),
              background: role === r.id ? "var(--accent-soft)" : "var(--bg-primary)",
              borderRadius: "var(--radius-md)",
              cursor: "pointer",
              alignItems: "flex-start",
            }}>
              <input type="radio" checked={role === r.id} onChange={() => setRole(r.id)} style={{
                appearance: "none",
                width: 16, height: 16, borderRadius: "50%",
                border: "1.5px solid " + (role === r.id ? "var(--accent)" : "var(--border-strong)"),
                background: role === r.id ? "var(--accent)" : "transparent",
                cursor: "pointer", marginTop: 1, position: "relative",
                boxShadow: role === r.id ? "inset 0 0 0 3px var(--bg-secondary)" : "none",
              }}/>
              <div>
                <div style={{fontSize: 13, fontWeight: 400}}>{t(r.label)}</div>
                <div className="muted" style={{fontSize: 12, marginTop: 2, lineHeight: 1.5}}>{t(r.desc)}</div>
                <div style={{display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8}}>
                  {r.perms.map(p => <span key={p} className="pill pill-neutral" style={{fontSize: 10}}>{t(p)}</span>)}
                </div>
              </div>
            </label>
          ))}
        </div>

      </div>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>{t("Cancel")}</button>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={busy}>
          {busy ? t('Adding…') : t('Add to team')}
        </button>
      </ModalFooter>
    </Modal>
  );
}

/* ============================================================
   KEYBOARD SHORTCUTS
   ============================================================ */
const SHORTCUTS = [
  { group: "Navigation", items: [
    [["G","S"],  "Go to Suppliers"],
    [["G","A"],  "Go to Approval queue"],
    [["G","B"],  "Go to Billing inbox"],
    [["G","R"],  "Go to Reports"],
    [["⌘","K"],  "Open command bar (search anything)"],
    [["⌘","/"],  "Show this shortcuts panel"],
    [["⌘","B"],  "Toggle sidebar"],
  ]},
  { group: "Suppliers list", items: [
    [["N"],            "New supplier"],
    [["F"],            "Focus filter"],
    [["⇧","Click"],    "Select range"],
    [["Space"],        "Toggle row"],
    [["↵"],            "Open selected supplier"],
    [["⌘","E"],        "Export current view"],
  ]},
  { group: "Invoice review", items: [
    [["A"],     "Approve"],
    [["R"],     "Reject"],
    [["⇧","M"], "Request more info"],
    [["J","K"], "Next / previous invoice"],
    [["⌘","↵"], "Approve and move to next"],
  ]},
  { group: "Theme & view", items: [
    [["⌘","⇧","L"], "Toggle light/dark theme"],
    [["⌘","⇧","D"], "Toggle density"],
    [["?"],         "Show this shortcuts panel"],
  ]},
];

export function ShortcutsModal({ open, onClose }) {
  const t = useT();
  return (
    <Modal open={open} onClose={onClose} width={680}>
      <ModalHeader title={t("Keyboard shortcuts")} subtitle={t("Move through the system without taking your hands off the keys.")} onClose={onClose}/>
      <div style={{padding: 22, overflowY: "auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22}}>
        {SHORTCUTS.map(s => (
          <div key={s.group}>
            <div className="t-eyebrow" style={{marginBottom: 12}}>{t(s.group)}</div>
            <div style={{display: "flex", flexDirection: "column", gap: 6}}>
              {s.items.map(([keys, label], i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "6px 0",
                }}>
                  <span style={{fontSize: 13, color: "var(--text-secondary)"}}>{t(label)}</span>
                  <span style={{display: "flex", gap: 4, alignItems: "center"}}>
                    {keys.map((k, j) => (
                      <React.Fragment key={j}>
                        <span style={{
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          minWidth: 22, height: 22, padding: "0 6px",
                          background: "var(--bg-primary)",
                          border: "1px solid var(--border-default)",
                          borderRadius: 4,
                          fontFamily: "var(--font-mono)", fontSize: 11,
                          color: "var(--text-primary)",
                          boxShadow: "0 1px 0 var(--border-subtle)",
                        }}>{k}</span>
                        {j < keys.length - 1 && k.length > 1 ? null : (j < keys.length - 1 && <span className="muted" style={{fontSize: 10}}>+</span>)}
                      </React.Fragment>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <ModalFooter>
        <span className="muted" style={{fontSize: 12, marginRight: "auto", alignSelf: "center"}}>
          {t("Hint: press")} <span className="kbd">?</span> {t("from anywhere")}
        </span>
        <button className="btn btn-primary btn-sm" onClick={onClose}>{t("Got it")}</button>
      </ModalFooter>
    </Modal>
  );
}

/* ============================================================
   PAY RUN SCHEDULER
   ============================================================ */
// ⚠ MODIFIED FROM PORT — accepts live `invoices` and wires the
// "Schedule N payments" button to api.batchScheduleInvoices.
export function PayRunModal({ open, onClose, invoices = [], onScheduled }) {
  const t = useT();
  // Only invoices in `approved-payment` can be scheduled
  const candidates = invoices.filter(i => i.status === 'approved-payment');
  const [selected, setSelected] = useStateMod(new Set());
  const [payDate, setPayDate] = useStateMod(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useStateMod(false);
  useEffectMod(() => {
    if (open) {
      setSelected(new Set(candidates.map(i => i.id || i.invoice_id)));
      setBusy(false);
    }
  }, [open]);

  const toggle = (id) => {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  };

  const selectedRows = candidates.filter(i => selected.has(i.id || i.invoice_id));
  const total = selectedRows.reduce((a, b) => a + netPayableOf(b), 0);
  const allOn = candidates.length > 0 && candidates.every(i => selected.has(i.id || i.invoice_id));

  const submit = async () => {
    if (!selected.size) {
      window.__toast?.('Select at least one invoice', 'error');
      return;
    }
    if (!payDate) {
      window.__toast?.('Pick a pay-out date', 'error');
      return;
    }
    setBusy(true);
    try {
      const result = await api.batchScheduleInvoices(Array.from(selected), payDate);
      window.__toast?.(`Scheduled ${result.scheduled} of ${result.total} payments`, 'success');
      onScheduled?.();
      onClose();
    } catch (e) {
      window.__toast?.(`Pay run failed: ${e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={busy ? () => {} : onClose} width={780}>
      <ModalHeader title={t("Schedule pay run")}
        subtitle={t("{a} of {b} approved invoices selected", { a: selected.size, b: candidates.length }) + ` · ${fmtTHB(total)} ${t("total")}`}
        onClose={busy ? () => {} : onClose}/>
      <div style={{display: "grid", gridTemplateColumns: "1fr 280px", flex: 1, overflow: "hidden"}}>
        <div style={{overflowY: "auto", borderRight: "1px solid var(--border-subtle)"}}>
          <div style={{padding: "14px 22px 6px", display: "flex", justifyContent: "space-between", alignItems: "center"}}>
            <div className="t-eyebrow">{t("Approved invoices ready to schedule")}</div>
            <button className="btn btn-ghost btn-sm" style={{height: 22, padding: "0 6px", fontSize: 11}}
              onClick={() => setSelected(allOn ? new Set() : new Set(candidates.map(i => i.id || i.invoice_id)))}>
              {allOn ? t("Deselect all") : t("Select all")}
            </button>
          </div>
          {candidates.length === 0 ? (
            <div style={{padding: 32, textAlign: "center", color: "var(--text-tertiary)", fontSize: 13}}>
              {t("No invoices in approved-payment yet. Approve invoices in the Billing inbox to make them schedulable here.")}
            </div>
          ) : (
            <table className="tbl" style={{tableLayout: "auto"}}>
              <thead>
                <tr>
                  <th style={{width: 32}}></th>
                  <th>{t("Invoice")}</th>
                  <th>{t("Vendor")}</th>
                  <th>{t("Project")}</th>
                  <th>{t("Due")}</th>
                  <th className="right">{t("Amount")}</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map(inv => {
                  const id = inv.id || inv.invoice_id;
                  return (
                    <tr key={id} className={selected.has(id) ? "selected" : ""} onClick={() => toggle(id)} style={{cursor: "pointer"}}>
                      <td><input type="checkbox" className="cb" checked={selected.has(id)} onChange={() => toggle(id)} onClick={e => e.stopPropagation()}/></td>
                      <td className="mono" style={{fontSize: 12, color: "var(--text-primary)"}}>{id}</td>
                      <td style={{fontSize: 13}}>
                        <div style={{display: "flex", alignItems: "center", gap: 8}}>
                          <Avatar name={inv.vendor || ''} size="sm"/>
                          <span className="ellipsis">{inv.vendor || inv.vendor_id}</span>
                        </div>
                      </td>
                      <td className="muted" style={{fontSize: 12}}>{inv.project || '—'}</td>
                      <td className="muted mono" style={{fontSize: 11}}>{inv.due ? new Date(inv.due).toLocaleDateString() : '—'}</td>
                      <td className="num right tnum">{fmtTHB(netPayableOf(inv))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={{padding: 18, overflowY: "auto", display: "flex", flexDirection: "column", gap: 16, background: "var(--bg-primary)"}}>
          <div>
            <div className="t-eyebrow" style={{marginBottom: 8}}>{t("Total to disburse")}</div>
            <div style={{fontSize: 28, fontWeight: 600, letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums"}}>{fmtTHB(total)}</div>
            <div className="muted" style={{fontSize: 11, marginTop: 4}}>
              {t("Across {a} invoices · {b} vendors", { a: selected.size, b: new Set(selectedRows.map(r => r.vendor_id || r.vid)).size })}
            </div>
          </div>

          <div className="field">
            <label className="field-label">{t("Pay-out date")}</label>
            <input type="date" className="input mono" value={payDate} onChange={e => setPayDate(e.target.value)}/>
            <span className="field-help">{t("Written to each invoice and to the Account Payable sheet's Due Date column.")}</span>
          </div>

          <div style={{
            padding: 12,
            background: "color-mix(in oklab, var(--info) 12%, transparent)",
            border: "1px solid color-mix(in oklab, var(--info) 30%, transparent)",
            borderRadius: "var(--radius-md)",
            fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5,
          }}>
            {t("Vendors will be notified the moment the run is signed off. Any disputed invoices stay excluded until resolved.")}
          </div>
        </div>
      </div>
      <ModalFooter>
        <span className="muted" style={{marginRight: "auto", alignSelf: "center", fontSize: 12}}>
          {selected.size > 0 && `${fmtTHB(total)} ${t("across {n} invoices", { n: selected.size })}`}
        </span>
        <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>{t("Cancel")}</button>
        <button className="btn btn-primary btn-sm" disabled={busy || !selected.size} onClick={submit}>
          {busy ? t('Scheduling…') : t("Schedule {n} payments", { n: selected.size })} <IconArrow size={12}/>
        </button>
      </ModalFooter>
    </Modal>
  );
}

/* ============================================================
   EDIT INVOICE DETAILS
   ============================================================ */
// Amend an invoice's submitted fields after it was created. Used in two places:
//   • mode="supplier" — the supplier portal, while the invoice is in pm-review.
//   • mode="staff"    — the internal Billing inbox, through 'scheduled'.
// The backend (api_updateInvoiceDetails) enforces who-can-edit-when; this form
// just collects the values. When a staff user edits an invoice that's already in
// an AP finance sheet, they can also correct its expense category, and the
// backend re-syncs the AP row.
export function EditInvoiceModal({ open, invoice, mode = 'staff', onClose, onSaved }) {
  const t = useT();
  const inv = invoice || {};
  const toDateInput = (v) => {
    if (!v) return '';
    const s = String(v);
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    const d = new Date(s);
    return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  };
  const [form, setForm] = useStateMod({});
  const [cats, setCats] = useStateMod([]);
  const [busy, setBusy] = useStateMod(false);
  const [error, setError] = useStateMod('');
  const showCategory = mode === 'staff' && !!inv.ap_company;

  useEffectMod(() => {
    if (!open) return;
    setForm({
      po_number:    inv.po_number != null ? inv.po_number : (inv.po || ''),
      project:      inv.project || '',
      amount_thb:   inv.amount_thb != null ? inv.amount_thb : (inv.amount != null ? inv.amount : ''),
      vat_thb:      inv.vat_thb != null ? inv.vat_thb : '',
      wht_thb:      inv.wht_thb != null ? inv.wht_thb : '',
      invoice_date: toDateInput(inv.invoice_date),
      due_at:       toDateInput(inv.due_at != null ? inv.due_at : inv.due),
      category:     inv.ap_category || '',
    });
    setError('');
    if (showCategory) {
      api.listExpenseCategories(inv.ap_company)
        .then(r => setCats((r && r.categories) || []))
        .catch(() => setCats([]));
    } else {
      setCats([]);
    }
  }, [open, inv.id, inv.invoice_id]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const netPreview = (parseMoney(form.amount_thb) || 0) + (parseMoney(form.vat_thb) || 0) - (parseMoney(form.wht_thb) || 0);

  const submit = async () => {
    setError('');
    // Money fields are free text (see MoneyInput) — parse before sending.
    const amount = parseMoney(form.amount_thb), vat = parseMoney(form.vat_thb), wht = parseMoney(form.wht_thb);
    if (!(amount > 0)) { setError(t('Amount must be a number greater than zero (e.g. 45000 or 45,000.00).')); return; }
    if (!Number.isFinite(vat)) { setError(t('VAT must be a number (enter 0 if not applicable).')); return; }
    if (!Number.isFinite(wht)) { setError(t('Withholding tax must be a number (enter 0 if not applicable).')); return; }
    setBusy(true);
    try {
      const fields = {
        po_number: form.po_number, project: form.project,
        amount_thb: amount, vat_thb: vat, wht_thb: wht,
        invoice_date: form.invoice_date, due_at: form.due_at,
      };
      if (showCategory && form.category) fields.category = form.category;
      await api.updateInvoiceDetails(inv.id || inv.invoice_id, fields);
      window.__toast?.(t('Invoice updated'), 'success');
      onSaved?.();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <Modal open={open} onClose={busy ? () => {} : onClose} width={620}>
      <ModalHeader title={t('Edit invoice details')} subtitle={inv.id || inv.invoice_id || ''} onClose={busy ? () => {} : onClose}/>
      <div style={{padding: "18px 22px", overflowY: "auto"}}>
        <div className="form-grid">
          <div className="field">
            <label className="field-label">{t('Invoice number')}</label>
            <input className="input mono" value={form.po_number} onChange={set('po_number')}/>
          </div>
          <div className="field">
            <label className="field-label">{t('Project')}</label>
            <input className="input" value={form.project} onChange={set('project')}/>
          </div>
          <div className="field">
            <label className="field-label">{t('Invoice date')}</label>
            <input type="date" className="input mono" value={form.invoice_date} onChange={set('invoice_date')}/>
          </div>
          <div className="field">
            <label className="field-label">{t('Due date')}</label>
            <input type="date" className="input mono" value={form.due_at} onChange={set('due_at')}/>
          </div>
          <div className="field">
            <label className="field-label">{t('Amount (Ex-VAT) THB')}</label>
            <MoneyInput className="input mono" value={form.amount_thb} onChange={set('amount_thb')}/>
          </div>
          <div className="field">
            <label className="field-label">{t('VAT THB')}</label>
            <MoneyInput className="input mono" value={form.vat_thb} onChange={set('vat_thb')}/>
          </div>
          <div className="field">
            <label className="field-label">{t('Withholding tax THB')}</label>
            <MoneyInput className="input mono" value={form.wht_thb} onChange={set('wht_thb')}/>
          </div>
          <div className="field">
            <label className="field-label">{t('Net payable')}</label>
            <input className="input mono" value={fmtTHB(netPreview)} readOnly disabled/>
          </div>
          {showCategory && (
            <div className="field full">
              <label className="field-label">{t('Expense category · Account Payable')}</label>
              <select className="input" value={form.category} onChange={set('category')}>
                <option value="">{t('Keep current category')}</option>
                {cats.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
        </div>
        {showCategory && (
          <div style={{marginTop: 12, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5}}>
            {t("This invoice is already in the {company} Account Payable sheet — your changes are synced to that row.", { company: inv.ap_company === 'brandstorm' ? 'Brandstorm' : 'MotionReach' })}
          </div>
        )}
        {error && (
          <div style={{marginTop: 14, padding: "10px 12px", background: "color-mix(in oklab, var(--danger) 12%, transparent)", color: "var(--danger)", borderRadius: 8, fontSize: 13}}>
            {error}
          </div>
        )}
      </div>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>{t('Cancel')}</button>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={busy}>
          {busy ? t('Saving…') : t('Save changes')}
        </button>
      </ModalFooter>
    </Modal>
  );
}

